import { del, get, post, put } from "../../helpers/utils/http-client";
import { APPS_ENDPOINT } from "../../consts/api-endpoints";

/**
 * Memory API.
 *
 * Two surfaces, matching botcircuits-backend/src/memory:
 *   - config  : cap, retention and instructions (one record per app)
 *   - entries : the stored notes themselves
 *
 * There is one memory, USER_MEMORY, partitioned per end user, so every entries
 * call must carry the `userId` the runtime knows the user by
 * (requestAttributes.userId). There is no shared, app-wide memory partition.
 *
 * Memory is on for every app. It can only be switched off by hand in the
 * database (`disabled: true` on the memory-config record), so the console has
 * no toggle.
 */

export const SCOPE_USER = "USER_MEMORY";

const memoryBase = (appId) => `${APPS_ENDPOINT}/${appId}/memory`;

// Reads and deletes carry the user as a query param; writes send it in the
// body.
//
// `get()` builds its own query string from the params argument, so reads pass
// a params object rather than a pre-built URL — appending `?userId=...` here
// would collide with that and produce a trailing `?` on the request.
const userParams = userId => (userId ? { userId } : {});

// `del()` takes no params argument, so deletes do have to carry the query
// string on the URL.
const withUser = (url, userId) =>
	userId ? `${url}${url.includes("?") ? "&" : "?"}userId=${encodeURIComponent(userId)}` : url;

export async function getMemoryConfig(appId) {
	try {
		const { response, status } = await get(`${memoryBase(appId)}/config`);
		if (status !== 200) {
			return null;
		}
		return response;
	} catch (err) {
		console.debug('getMemoryConfig failed cause', err.message, err.code);
		return null;
	}
}

export async function saveMemoryConfig(appId, data) {
	try {
		return await put(`${memoryBase(appId)}/config`, data);
	} catch (err) {
		console.debug('saveMemoryConfig failed cause', err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

/**
 * A user's entries, together with the capacity figures the page shows as a
 * usage bar. Returns an empty shape on failure so the page can render.
 */
export async function getMemoryEntries(appId, scope, userId) {
	try {
		const { response, status } = await get(
			`${memoryBase(appId)}/${scope}/entries`,
			userParams(userId)
		);
		if (status !== 200) {
			return { scope, entries: [], usedChars: 0, capChars: 0, usagePct: 0 };
		}
		return response;
	} catch (err) {
		console.debug('getMemoryEntries failed cause', err.message, err.code);
		return { scope, entries: [], usedChars: 0, capChars: 0, usagePct: 0 };
	}
}

export async function createMemoryEntry(appId, scope, data) {
	try {
		return await post(`${memoryBase(appId)}/${scope}/entries`, data);
	} catch (err) {
		console.debug('createMemoryEntry failed cause', err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

export async function updateMemoryEntry(appId, scope, entryId, data) {
	try {
		return await put(`${memoryBase(appId)}/${scope}/entries/${entryId}`, data);
	} catch (err) {
		console.debug('updateMemoryEntry failed cause', err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

export async function deleteMemoryEntry(appId, scope, entryId, userId) {
	try {
		return await del(
			withUser(`${memoryBase(appId)}/${scope}/entries/${entryId}`, userId)
		);
	} catch (err) {
		console.debug('deleteMemoryEntry failed cause', err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}

/** Wipe a user's memory — "forget this user". */
export async function clearMemoryScope(appId, scope, userId) {
	try {
		return await del(withUser(`${memoryBase(appId)}/${scope}/entries`, userId));
	} catch (err) {
		console.debug('clearMemoryScope failed cause', err.message, err.code);
		return { response: err.displayError, status: err.code };
	}
}
